import { isDate, format, parseISO, isBefore } from 'date-fns';
import validator from 'validator';
import brnv from 'bank-routing-number-validator';
import creditCardType from 'credit-card-type';
import { validateEmail} from "../global/utils";

// 3.3.1 wants an error to say which field it is about, and a single concatenated
// string cannot: the caller also has to know which control to mark aria-invalid
// and which one to move focus to. So validation returns one entry per problem,
// each carrying the id of the control at fault, and the string form the callers
// already used is just those messages joined for the summary Alert.
//
// Messages name the field using the words on its visible label, so that what a
// screen reader reads back matches what the user is looking at.
const fieldError = (field, message) => ({ field, message });

export const joinFieldErrors = (fieldErrors) => fieldErrors.map((e) => e.message).join('; ');

// Shared by both payment types: the contact rules are identical, and duplicating
// them is how the two lists drifted apart in the first place.
const addContactErrors = (errors, paymentFields, court) => {
    if (paymentFields.email && !validateEmail(paymentFields.email)) {
        errors.push(fieldError('email', 'The Email Address is invalid'));
    }
    if (paymentFields.phone && paymentFields.phone.replace(/\D/g, '').length !== 10) {
        errors.push(fieldError('phone', 'The Phone Number must be 10 digits'));
    }
    if (court.requireContactInfo && !paymentFields.email && !paymentFields.phone) {
        errors.push(fieldError('email',
            `${court.courtName} requires that you enter either an email address or a phone number`));
    }
    // ZIP+4 is allowed through because a prefilled zip from the lookup can be one.
    if (paymentFields.billingZip && !/^\d{5}(\d{4})?$/.test(paymentFields.billingZip.replace('-', ''))) {
        errors.push(fieldError('billingZip', 'The Billing Zip must be 5 digits'));
    }
}

export const validateCardFields = (paymentFields, court) => {
    const errors = [];
    const cardTypeVal = creditCardType(paymentFields.cardNumber);
    const cardType = Array.isArray(cardTypeVal) && cardTypeVal.length > 0 ? cardTypeVal[0] : '';
    // The label and aria-required have always advertised this as required; until
    // CPDEV-2777 nothing enforced it, so a blank name reached the gateway.
    if (!paymentFields.payorName) {
        errors.push(fieldError('name', 'The Name on Card is required'));
    }
    if (!paymentFields.cardNumber) {
        errors.push(fieldError('cardNumber', 'The card number is required'));
    } else if (!validator.isCreditCard(paymentFields.cardNumber)) {
        errors.push(fieldError('cardNumber', 'The card number is invalid'));
    } else if (!cardType || (cardType.type !== 'visa' && cardType.type !== 'mastercard' &&
            cardType.type !== 'discover' && cardType.type !== 'american-express')) {
        errors.push(fieldError('cardNumber', cardType && cardType.niceType
            ? `${cardType.niceType} cards are not accepted at CitePay`
            : 'Your card is not one of the types accepted at CitePay'));
    }
    if (!paymentFields.expDate) {
        errors.push(fieldError('cardExp', 'The card expiration date is required'));
    } else if (!isDate(paymentFields.expDate)) {
        errors.push(fieldError('cardExp', 'The card expiration date is invalid'));
    } else if (isBefore(paymentFields.expDate, new Date())) {
        errors.push(fieldError('cardExp', 'The card expiration date must be in the future'));
    }
    // Separating empty from wrong-length matters here: "invalid" on an untouched
    // field reads as a complaint about something the user has not done yet.
    const cvcDigits = cardType.type === 'american-express' ? 4 : 3;
    if (!paymentFields.cvv2) {
        errors.push(fieldError('cvv2', 'The CVC is required'));
    } else if (paymentFields.cvv2.length !== cvcDigits) {
        errors.push(fieldError('cvv2', `The CVC must be ${cvcDigits} digits`));
    }
    if (court.requireZip && !paymentFields.billingZip) {
        errors.push(fieldError('billingZip', 'The Billing Zip is required'));
    }
    if (cardType.type === 'american-express' && !court.allowAmex) {
        errors.push(fieldError('cardNumber', `The ${court.courtName} does not accept American Express`));
    }
    addContactErrors(errors, paymentFields, court);
    return errors;
}

export const validateCardData = (paymentFields, court) =>
    joinFieldErrors(validateCardFields(paymentFields, court));

export const validateCheckFields = (paymentFields, court) => {
    const errors = [];
    if (!paymentFields.payorName) {
        errors.push(fieldError('name', 'The Name on Account is required'));
    }
    if (!paymentFields.routingNumber) {
        errors.push(fieldError('routingNumber', 'The routing number is required'));
    } else if (!brnv.ABARoutingNumberIsValid(paymentFields.routingNumber)) {
        errors.push(fieldError('routingNumber', 'The routing number is invalid'));
    }
    if (!paymentFields.bankAccountNumber || paymentFields.bankAccountNumber.length < 4) {
        errors.push(fieldError('accountNumber',
            'The bank account number must contain at least four characters'));
    }
    addContactErrors(errors, paymentFields, court);
    return errors;
}

export const validateCheckData = (paymentFields, court) =>
    joinFieldErrors(validateCheckFields(paymentFields, court));

export const validateContactInfoAndZeroCheckFields = (paymentRequest, court) => {
    const errors = [];
    if (paymentRequest.totalAmount <= 0) {
        // Not attributable to a control on this form -- there is nothing to focus.
        errors.push(fieldError(null, 'The payment amount must be greater than zero'));
    }
    if (court.requireContactInfo && !paymentRequest.email && !paymentRequest.phone) {
        errors.push(fieldError('email', 'Either an email address or a phone number is required'));
    }
    if (court.requireAddress) {
        // Naming the fields that are actually blank, rather than "all address
        // information", is the difference between a message the user can act on
        // and one they have to guess at.
        [
            ['payorAddress', 'address', 'Billing Address'],
            ['payorCity', 'city', 'Billing City'],
            ['payorState', 'state', 'Billing State'],
            ['billingZip', 'billingZip', 'Billing Zip'],
        ].forEach(([prop, field, label]) => {
            if (!paymentRequest[prop]) errors.push(fieldError(field, `The ${label} is required`));
        });
    }
    return errors;
}

export const validateContactInfoAndZeroCheck = (paymentRequest, court) =>
    joinFieldErrors(validateContactInfoAndZeroCheckFields(paymentRequest, court));

export const getDisplayTime = (timeIn) => {
    if (timeIn) {
        return format(parseISO(timeIn), 'MM/dd/yyyy hh:mm:ss aa')
    }
}

export const getReceiptRequest = (paymentRequest, paymentResponse) => {
    const request = {
        courtId: paymentRequest.courtId,
        retrievalCode: paymentResponse.retrievalCode,
        unpaidBalances: paymentRequest.unpaidBalances ? paymentRequest.unpaidBalances : null,
        paymentPlanId: paymentRequest.paymentPlans && paymentRequest.paymentPlans.length > 0
            ? paymentRequest.paymentPlans[0].planId
            : null
    }
    return JSON.stringify(request);
}

export const getFeeQuery = (court, courtAmt, paymentType) => {
    const query = {
        courtId: court.courtId,
        paymentCategory: 'online',
        paymentType: paymentType,
        courtAmount: courtAmt
    }
    return JSON.stringify(query);
}

export const validateVerify = (verifyFields) => {
    let errors = '';
    if (!verifyFields.channel) {
        errors = 'The contact method is required';
    }
    if (!verifyFields.phone) {
        errors = 'The phone number is required';
    } else if (verifyFields.phone.length < 10) {
        errors = 'The phone number is invalid';
    }
    return errors;
}
